/**
 * Cart API Service
 * Refactored from legacy connectionManager
 *
 * Purpose: Manage shopping cart operations (read, delete, CMS products)
 *
 * Dependencies:
 * - @repo/shared-utils/cache - Function memoization and cache clearing
 * - @repo/shared-utils/session - User session management
 * - @repo/shared-utils/cart - Cart utilities and constants
 */

import {
  clearCacheForFunction,
  clearCacheForKey,
  createCacheAsync,
} from "@repo/shared-utils/cache";
import { getSessionData } from "@repo/shared-utils/session";
import {
  getItemsInCart,
  PROD_CODES,
  CART_CONSTANTS,
  DEFAULT_BALIC_CART_AMOUNT,
} from "@repo/shared-utils/cart";
import { request } from "../core/request";
import {
  API_RESPONSE_CODES,
  DEFAULT_CONNECTION_FROM,
  OPERATION_IDS,
  REST_CONNECTION_FROM,
} from "./constants";
import { IResponse } from "../types/apiTypes";
import {
  IApiResponse,
  ICartData,
  ICmsCartResponse,
  IDeleteFromCartRequest,
  IDeleteFromCartResponse,
} from "../types/cartTypes";

// Get APP_SRC from URL params (legacy constant reference)
const APP_SRC = "appSource";

/**
 * Get user's shopping cart
 *
 * Features:
 * - Fetches cart from connection-manager API
 * - Caches results per mobile number
 * - Special handling for BALIC products (sets default amount)
 * - Returns empty cart on errors
 *
 * @returns Promise<ICartData> - Cart details with count
 *
 * @example
 * ```typescript
 * const cart = await getCart();
 * console.log("Cart items:", cart.data.cartDetails);
 * console.log("Total items:", cart.data.cartCount);
 * ```
 */
export const getCart = async (): Promise<ICartData> => {
  try {
    const { mobileNo } = await getSessionData();
    const urlParams = new URLSearchParams(window.location.search);
    const channel = urlParams.get(APP_SRC) || "";

    const response = (await createCacheAsync(
      `${OPERATION_IDS.readFromCart}-${mobileNo}`,
      request<IApiResponse, { mobileNo: string; channel: string }>,
      {
        operationId: OPERATION_IDS.readFromCart,
        requestData: {
          mobileNo,
          channel,
        },
        connectionFrom: DEFAULT_CONNECTION_FROM,
      }
    )) as IApiResponse;

    let { statusCode, cartDetails } = response;

    if (statusCode === API_RESPONSE_CODES.STATUS_CODE_7001) {
      // Handle Balic cart products and PSBL handling
      cartDetails = cartDetails?.map((cartData) => {
        const isBalic = cartData?.prod === PROD_CODES.BALIC?.value;
        return {
          ...cartData,
          amt: isBalic ? String(DEFAULT_BALIC_CART_AMOUNT) : cartData.amt,
        };
      });

      return {
        data: { cartDetails, cartCount: cartDetails?.length },
        statusCode,
      } as ICartData;
    }

    if (statusCode === API_RESPONSE_CODES.STATUS_CODE_7002) {
      return {
        data: { cartDetails: [], cartCount: 0 },
        statusCode: API_RESPONSE_CODES.STATUS_CODE_7001,
      } as ICartData;
    }

    throw new Error(JSON.stringify(response));
  } catch (error) {
    console.error("[Cart Service] Error fetching cart:", error);
    return {
      data: { cartDetails: [], cartCount: 0 },
      statusCode: API_RESPONSE_CODES.STATUS_CODE_7002,
    } as ICartData;
  }
};

/**
 * Get CMS cart products configuration
 *
 * @returns Promise<ICmsCartResponse> - CMS cart products data
 *
 * @example
 * ```typescript
 * const cmsProducts = await getCmsCartProducts();
 * ```
 */
export const getCmsCartProducts = async (): Promise<ICmsCartResponse> => {
  try {
    const data = {
      prodcode: CART_CONSTANTS.ALL,
      channel: CART_CONSTANTS.WEB,
    };
    const response = await request({
      operationId: OPERATION_IDS.cmsCartProducts,
      requestData: data,
      connectionFrom: REST_CONNECTION_FROM,
    });

    return response as ICmsCartResponse;
  } catch (error) {
    console.error("[Cart Service] Error fetching CMS cart products:", error);
    throw error;
  }
};

/**
 * Delete item from cart
 *
 * @param data - Delete request data (product code, mobile number, etc.)
 * @returns Promise<IDeleteFromCartResponse> - Delete response
 *
 * @example
 * ```typescript
 * const result = await deleteFromCartApi({
 *   mobileNo: "9876543210",
 *   prodcode: "HL"
 * });
 * ```
 */
export const deleteFromCartApi = async (
  data: IDeleteFromCartRequest
): Promise<IResponse<IDeleteFromCartResponse>> => {
  try {
    const response = (await request({
      operationId: OPERATION_IDS.deleteFromCart,
      requestData: { ...data },
    })) as IDeleteFromCartResponse;

    if (response.statuscode !== "7001") {
      throw new Error(JSON.stringify(response));
    }

    return response;
  } catch (error) {
    console.error("[Cart Service] Error deleting from cart:", error);
    throw error;
  }
};

/**
 * Clear cart cache for a user
 *
 * Call this after cart modifications to force fresh data on next fetch
 *
 * @param mobileNo - User's mobile number
 *
 * @example
 * ```typescript
 * // After adding/removing items
 * deleteCartApiFromCm("9876543210");
 * ```
 */
export const deleteCartApiFromCm = (mobileNo: string): void => {
  clearCacheForKey(
    `${OPERATION_IDS.readFromCart}-${mobileNo}`,
    request<IApiResponse, { mobileNo: string; channel: string }>
  );
  clearCacheForKey(mobileNo, getCart);
  clearCacheForFunction(getItemsInCart);
};

/**
 * Get CMS cart products
 *
 * @param mobileNo - User's mobile number
 * @returns Promise with CMS cart products
 */
export async function getCMSCartProducts(mobileNo: string): Promise<unknown> {
  return request({
    operationId: OPERATION_IDS.cmsCartProducts,
    requestData: {
      mobileNo,
    },
    connectionFrom: DEFAULT_CONNECTION_FROM,
  });
}
